// Payment Monitor Service
// Real-time Bitcoin transaction monitoring using Boar Network WebSocket API

import { BOAR_CONFIG, BoarMessage, BoarTransaction, AddressSubscription, BoarError, BOAR_ERRORS } from '@/lib/boar-config';
import { transactionStorage, StoredTransaction } from './transaction-storage';
import { boarRPC } from './boar-rpc';
import { invoiceStorage } from './invoice-storage';
import { Invoice } from '@/types/invoice';
import { parseEther } from 'viem';

export interface PaymentEvent {
  type: 'payment_detected' | 'payment_confirmed' | 'connection_status';
  invoiceId: string;
  transaction?: StoredTransaction;
  status?: 'connected' | 'disconnected' | 'reconnecting';
  error?: string;
}

export interface PaymentMonitorCallbacks {
  onPaymentDetected?: (event: PaymentEvent) => void;
  onPaymentConfirmed?: (event: PaymentEvent) => void;
  onConnectionStatus?: (event: PaymentEvent) => void;
  onError?: (error: BoarError) => void;
}

class PaymentMonitorService {
  private ws: WebSocket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private subscriptions = new Map<string, AddressSubscription>();
  private callbacks: PaymentMonitorCallbacks = {};
  private heartbeatInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Only connect if we're in a browser environment
    if (typeof window !== 'undefined') {
      this.connect();
    }
  }

  // Set event callbacks
  setCallbacks(callbacks: PaymentMonitorCallbacks): void {
    // Merge with existing callbacks instead of replacing
    this.callbacks = { ...this.callbacks, ...callbacks };
    console.log('📝 Payment monitor callbacks updated');
  }

  // Connect to Boar WebSocket API
  private connect(): void {
    try {
      console.log('🔌 Connecting to Boar Network WebSocket...');
      console.log('🌐 WebSocket URL:', BOAR_CONFIG.wsUrl);
      
      // Connect to Boar Network WebSocket (API key is already in the URL)
      this.ws = new WebSocket(BOAR_CONFIG.wsUrl);
      
      this.ws.onopen = () => {
        console.log('✅ Connected to Boar Network WebSocket');
        console.log('🔗 Boar RPC endpoint:', BOAR_CONFIG.httpUrl);
        console.log('🔗 Boar WebSocket endpoint:', BOAR_CONFIG.wsUrl);
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.resubscribeAll();
        this.notifyConnectionStatus('connected');
      };
      
      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };
      
      this.ws.onclose = () => {
        console.log('🔌 Boar WebSocket connection closed');
        this.isConnected = false;
        this.stopHeartbeat();
        this.scheduleReconnect();
      };
      
      this.ws.onerror = (error) => {
        console.error('🚨 Boar WebSocket error:', error);
        this.notifyError(new BoarError('WebSocket error', BOAR_ERRORS.CONNECTION_FAILED, error));
      };
      
    } catch (error) {
      console.error('🚨 Failed to connect to Boar Network:', error);
      this.notifyError(new BoarError('Failed to connect', BOAR_ERRORS.CONNECTION_FAILED, error));
    }
  }

  // Handle incoming WebSocket messages
  private handleMessage(data: string): void {
    try {
      // Handle ping/pong messages
      if (data === 'ping' || data === 'pong') {
        return;
      }
      
      const parsed = JSON.parse(data);
      
      // Check if it's a JSON-RPC error
      if (parsed.error) {
        // Ignore subscription format errors - Boar doesn't support standard eth_subscribe
        if (parsed.error.code === -32600) {
          console.log('📨 Boar subscription error (expected - using RPC-based monitoring instead):', parsed.error.message);
          return;
        }
        console.error('🚨 Boar WebSocket error:', parsed.error);
        return;
      }
      
      const message: BoarMessage = parsed;
      if (!message.type) {
        console.log('📨 Received message without type (may be subscription confirmation):', data.substring(0, 200));
        return;
      }
      
      switch (message.type) {
        case 'transaction':
          this.handleTransaction(message.data as BoarTransaction);
          break;
        case 'block':
          this.handleBlock(message.data);
          break;
        case 'error':
          this.handleError(message.data);
          break;
        default:
          console.log('📨 Received message:', message.type);
      }
    } catch (error) {
      console.error('🚨 Failed to parse WebSocket message:', error);
      console.error('📨 Raw data:', data);
      this.notifyError(new BoarError('Invalid message format', BOAR_ERRORS.INVALID_MESSAGE, error));
    }
  }

  // Handle transaction notifications
  private handleTransaction(transaction: BoarTransaction): void {
    console.log('💰 Mezo testnet transaction detected:', transaction.hash);
    console.log('   From:', transaction.from);
    console.log('   To:', transaction.to);
    console.log('   Value:', transaction.value);
    
    // Find matching subscription
    const subscription = Array.from(this.subscriptions.values())
      .find(sub => sub.address.toLowerCase() === transaction.to.toLowerCase());
    
    if (subscription) {
      console.log('🎯 Transaction matches invoice:', subscription.invoiceId);
      console.log('   Expected address:', subscription.address);
      
      // Check amount if expected amount is specified
      if (subscription.expectedAmount) {
        const receivedAmount = BigInt(transaction.value);
        const expectedAmount = BigInt(subscription.expectedAmount);
        
        console.log('   Expected amount:', subscription.expectedAmount);
        console.log('   Received amount:', transaction.value);
        
        // Payment must be >= expected amount (can be more, not less)
        if (receivedAmount < expectedAmount) {
          console.log('   ⚠️ Payment amount too low. Expected:', subscription.expectedAmount, 'Got:', transaction.value);
          console.log('   Shortfall:', (expectedAmount - receivedAmount).toString(), 'wei');
          return;
        }
        
        // Check if this is an overpayment
        if (receivedAmount > expectedAmount) {
          const overpayment = receivedAmount - expectedAmount;
          console.log('   💰 Overpayment detected! Extra amount:', overpayment.toString(), 'wei');
          console.log('   ✅ Payment accepted with overpayment (received > expected)');
        } else {
          console.log('   ✅ Payment amount accepted (received = expected)');
        }
      }
      
      // Store transaction
      const storedTransaction: StoredTransaction = {
        txHash: transaction.hash,
        invoiceId: subscription.invoiceId,
        from: transaction.from,
        to: transaction.to,
        amount: transaction.value,
        blockNumber: transaction.blockNumber,
        timestamp: transaction.timestamp,
        confirmations: transaction.confirmations,
        status: transaction.status,
        detectedAt: Date.now(), // number, not string
      };
      
      // Add transaction with creator address for scoped storage
      if (subscription.creator) {
        transactionStorage.addTransaction(subscription.creator, storedTransaction);
        
        // Automatically mark invoice as paid (scoped by creator wallet)
        invoiceStorage.markAsPaid(subscription.creator, subscription.invoiceId, transaction.value, transaction.hash);
      }
      
      // Unsubscribe from this address since payment is complete
      this.unsubscribeFromAddress(subscription.address);
      
      // Notify payment detected
      this.notifyPaymentDetected(subscription.invoiceId, storedTransaction);
    }
  }

  // Handle new block notifications
  // Note: This method cannot update transactions without knowing the creator address
  // Transactions are updated individually when detected, not in bulk here
  private handleBlock(_blockData: unknown): void {
    // Block handling is currently disabled because we need wallet addresses to update transactions
    // Individual transactions are updated when detected in handleTransaction
  }

  // Handle error messages
  private handleError(errorData: unknown): void {
    console.error('🚨 Boar API error:', errorData);
    this.notifyError(new BoarError((errorData as { message?: string }).message || 'Unknown error', (errorData as { code?: string }).code || 'UNKNOWN_ERROR', errorData));
  }

  // Subscribe to monitor an address
  subscribeToAddress(subscription: AddressSubscription): void {
    // Validate EVM address format to avoid provider errors
    const isEvmAddress = typeof subscription.address === 'string' && /^0x[a-fA-F0-9]{40}$/.test(subscription.address);
    if (!isEvmAddress) {
      console.warn('⚠️ Skipping subscription: invalid EVM address for Mezo testnet', subscription.address);
      return;
    }
    console.log('👀 SUBSCRIBING to Mezo testnet address:', subscription.address, 'for invoice:', subscription.invoiceId);
    console.log('   Current connection status:', this.isConnected);
    console.log('   Active subscriptions before:', this.subscriptions.size);
    
    this.subscriptions.set(subscription.address.toLowerCase(), subscription);
    
    if (this.isConnected && this.ws) {
      this.sendSubscription(subscription.address);
      console.log('✅ Subscription sent to Boar Network');
    } else {
      // If not connected yet, it will resubscribe when connection is established
      console.log('⏳ Waiting for WebSocket connection to subscribe...');
      console.log('   Will resubscribe when connection is established');
    }
    console.log('   Active subscriptions after:', this.subscriptions.size);
  }


  // Unsubscribe from monitoring an address
  unsubscribeFromAddress(address: string): void {
    console.log('👋 Unsubscribing from address:', address);
    
    this.subscriptions.delete(address.toLowerCase());
    
    if (this.isConnected && this.ws) {
      this.sendUnsubscription(address);
    }
  }

  // Send subscription request
  // NOTE: Boar Network doesn't support standard JSON-RPC eth_subscribe format
  // Payment monitoring is handled via RPC polling in confirmInvoicePaid instead
  private sendSubscription(address: string): void {
    const isEvmAddress = typeof address === 'string' && /^0x[a-fA-F0-9]{40}$/.test(address);
    if (!isEvmAddress) {
      console.warn('⚠️ Not sending subscription for invalid EVM address:', address);
      return;
    }
    // Boar Network WebSocket doesn't support standard eth_subscribe
    // We rely on RPC-based payment confirmation instead
    console.log('📡 Address added to monitoring list:', address);
    console.log('   Payment will be verified via RPC when "Mark as Paid" is clicked');
  }

  // Send unsubscription request
  private sendUnsubscription(address: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message: BoarMessage = {
        type: 'unsubscribe',
        address: address,
      };
      this.ws.send(JSON.stringify(message));
    }
  }

  // Resubscribe to all addresses after reconnection
  private resubscribeAll(): void {
    console.log('🔄 Resubscribing to all addresses...');
    this.subscriptions.forEach((_subscription, address) => {
      this.sendSubscription(address);
    });
  }

  // Schedule reconnection attempt
  private scheduleReconnect(): void {
    if (this.reconnectAttempts < BOAR_CONFIG.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = BOAR_CONFIG.reconnectInterval * this.reconnectAttempts;
      
      console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${BOAR_CONFIG.maxReconnectAttempts})`);
      
      this.reconnectTimer = setTimeout(() => {
        this.notifyConnectionStatus('reconnecting');
        this.connect();
      }, delay);
    } else {
      console.error('🚨 Max reconnection attempts reached');
      this.notifyError(new BoarError('Max reconnection attempts reached', BOAR_ERRORS.CONNECTION_FAILED));
    }
  }

  // Start heartbeat to keep connection alive
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Send ping every 30 seconds
  }

  // Stop heartbeat
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Notify payment detected
  private notifyPaymentDetected(invoiceId: string, transaction: StoredTransaction): void {
    const event: PaymentEvent = {
      type: 'payment_detected',
      invoiceId,
      transaction,
    };
    
    this.callbacks.onPaymentDetected?.(event);
  }

  // Notify payment confirmed
  // NOTE: Currently not called in the flow but kept for callback interface compatibility
  // @ts-expect-error - Method kept for callback interface compatibility but not currently used
  private _notifyPaymentConfirmed(invoiceId: string, transaction: StoredTransaction): void {
    const event: PaymentEvent = {
      type: 'payment_confirmed',
      invoiceId,
      transaction,
    };
    
    this.callbacks.onPaymentConfirmed?.(event);
  }

  // Notify connection status
  private notifyConnectionStatus(status: 'connected' | 'disconnected' | 'reconnecting'): void {
    const event: PaymentEvent = {
      type: 'connection_status',
      invoiceId: '',
      status,
    };
    
    this.callbacks.onConnectionStatus?.(event);
  }

  // Notify error
  private notifyError(error: BoarError): void {
    this.callbacks.onError?.(error);
  }

  // Get connection status
  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  // Get active subscriptions count
  getActiveSubscriptions(): number {
    return this.subscriptions.size;
  }

  // Get subscription info
  getSubscription(address: string): AddressSubscription | undefined {
    return this.subscriptions.get(address.toLowerCase());
  }

  // Confirm if an invoice has been paid using Boar RPC
  async confirmInvoicePaid(invoice: Invoice): Promise<{ confirmed: boolean; amount: string; error?: string; shortfall?: string; transaction?: StoredTransaction }> {
    try {
      console.log('🔍 ===== PAYMENT CONFIRMATION DEBUG =====');
      console.log('🔍 Checking payment for invoice:', invoice.id);
      console.log('🔍 Invoice payToAddress:', invoice.payToAddress);
      console.log('🔍 Invoice bitcoinAddress:', invoice.bitcoinAddress);
      console.log('🔍 Invoice requestedAmount:', invoice.requestedAmount);
      console.log('🔍 Invoice balanceAtCreation:', invoice.balanceAtCreation);
      console.log('🔍 Invoice expiresAt:', invoice.expiresAt);
      console.log('🔍 Invoice type:', typeof invoice.payToAddress);
      
      // Proceed with HTTP balance verification regardless of WebSocket status
      console.log('🔍 Proceeding with HTTP balance verification (WS status not required)');
      
      // Use payToAddress if available, otherwise fall back to bitcoinAddress
      let paymentAddress = invoice.payToAddress || invoice.bitcoinAddress;
      
      // Ensure paymentAddress is a string and not undefined/null
      if (!paymentAddress || typeof paymentAddress !== 'string') {
        console.log('❌ No valid payment address found in invoice');
        console.log('   payToAddress:', invoice.payToAddress, 'type:', typeof invoice.payToAddress);
        console.log('   bitcoinAddress:', invoice.bitcoinAddress, 'type:', typeof invoice.bitcoinAddress);
        return { confirmed: false, amount: '0', error: 'No valid payment address found' };
      }
      
      // Trim whitespace and ensure it's a string
      paymentAddress = String(paymentAddress).trim();
      
      // Validate EVM address for Mezo testnet
      const isEvmAddress = /^0x[a-fA-F0-9]{40}$/.test(paymentAddress);
      if (!isEvmAddress) {
        console.log('❌ Payment address is not a valid EVM address for Mezo:', paymentAddress);
        return { confirmed: false, amount: '0', error: 'Invalid Mezo address' };
      }
      
      console.log('🔍 Using payment address:', paymentAddress);
      
      // Check if invoice is still valid (not expired)
      const now = new Date();
      const expiresAt = new Date(invoice.expiresAt);
      
      if (now > expiresAt) {
        console.log('⏰ Invoice has expired, cannot confirm payment');
        return { confirmed: false, amount: '0', error: 'Invoice has expired' };
      }

      // Get current balance for the payment address via HTTP RPC
      console.log('🔍 Calling Boar RPC to get balance for address:', paymentAddress);
      const balance = await boarRPC.getAddressBalance(paymentAddress);
      const currentBalance = BigInt(balance.balance);
      
      console.log('💰 Boar RPC Response:', balance);
      console.log('💰 Current balance for', paymentAddress, ':', balance.balance);
      console.log('💰 Current balance (BigInt):', currentBalance.toString());
      
      // Try to get recent transactions to detect incoming payments (even if balance hasn't updated yet)
      let detectedPaymentAmount = 0n;
      let transactions: BoarTransaction[] = [];
      let incomingTxs: BoarTransaction[] = [];
      try {
        console.log('🔍 Checking transaction history for incoming payments...');
        transactions = await boarRPC.getAddressTransactions(paymentAddress);
        console.log('📝 Found transactions:', transactions.length);
        
        // Filter for transactions TO this address (incoming)
        incomingTxs = transactions.filter(tx => tx.to?.toLowerCase() === paymentAddress.toLowerCase() && BigInt(tx.value || '0') > 0n);
        console.log('💰 Incoming transactions:', incomingTxs.length);
        
        if (incomingTxs.length > 0) {
          // Sum up all incoming transactions
          detectedPaymentAmount = incomingTxs.reduce((sum, tx) => sum + BigInt(tx.value || '0'), 0n);
          console.log('💰 Total detected from transactions:', detectedPaymentAmount.toString());
        }
      } catch (txError) {
        console.warn('⚠️ Could not fetch transaction history (may not be supported):', txError);
      }
      
      // Handle both wei format (preferred) and decimal string (legacy)
      // If the string contains a decimal point, parse as decimal; otherwise treat as wei
      let requestedAmountWei: bigint;
      if (invoice.requestedAmount && invoice.requestedAmount.includes('.')) {
          requestedAmountWei = parseEther(invoice.requestedAmount);
        } else {
        try {
          requestedAmountWei = BigInt(invoice.requestedAmount);
        } catch {
          requestedAmountWei = 0n;
        }
      }
      
      // Parse and normalise balance snapshot from creation.
      // NOTE: A snapshot of '0' is VALID if the wallet really had zero balance then.
      // We only treat the snapshot as missing when it is undefined/empty or cannot be parsed.
      let creationBalance: bigint | null = null;
      if (invoice.balanceAtCreation !== undefined && invoice.balanceAtCreation !== null && invoice.balanceAtCreation !== '') {
        try {
          // Support both hex and decimal formats
          const raw = invoice.balanceAtCreation;
          creationBalance = raw.startsWith('0x') ? BigInt(raw) : BigInt(raw);
        } catch (e) {
          console.warn('⚠️ Invalid balanceAtCreation snapshot, ignoring:', invoice.balanceAtCreation, e);
          creationBalance = null;
        }
      }
      
      const hasValidBalanceSnapshot = creationBalance !== null;
      
      // ALWAYS prioritize transaction history over balance increase
      // This ensures we only confirm the actual payment amount, not the full wallet balance
      let totalReceived: bigint = 0n;
      
      if (detectedPaymentAmount > 0n) {
        // We have transaction history - use it (most accurate)
        totalReceived = detectedPaymentAmount;
        
        console.log('📊 ===== PAYMENT DETECTION (TRANSACTION HISTORY) =====');
        console.log('📊 Using transaction history (most accurate method)');
        console.log('📊 Detected from transactions:', detectedPaymentAmount.toString());
        console.log('📊 Total received (from transaction history):', totalReceived.toString());
        
        if (hasValidBalanceSnapshot) {
          const balanceIncrease = currentBalance - creationBalance!;
          console.log('📊 Balance snapshot info (for reference only):');
          console.log('   Creation balance:', invoice.balanceAtCreation);
          console.log('   Current balance:', currentBalance.toString());
          console.log('   Balance increase:', balanceIncrease.toString());
          console.log('   ⚠️ NOT using balance increase - using transaction history instead');
        }
      } else if (hasValidBalanceSnapshot) {
        // Fallback: Use balance increase only if no transaction history available
        const balanceIncrease = currentBalance - creationBalance!;
        
        if (balanceIncrease > 0n) {
          // Cap the amount to the requested amount to avoid showing full wallet balance
          totalReceived = balanceIncrease > requestedAmountWei ? requestedAmountWei : balanceIncrease;
          
          console.log('📊 ===== PAYMENT DETECTION (BALANCE INCREASE - FALLBACK) =====');
          console.log('⚠️ No transaction history found - using balance increase as fallback');
          console.log('📊 Creation balance:', invoice.balanceAtCreation);
          console.log('📊 Current balance:', currentBalance.toString());
          console.log('📊 Balance increase:', balanceIncrease.toString());
          console.log('📊 Requested amount:', requestedAmountWei.toString());
          console.log('📊 Total received (capped to requested):', totalReceived.toString());
        }
      } else {
        // No transaction history AND no reliable snapshot. As a last resort we can still
        // look at the absolute balance and compare it to the requested amount. This is
        // slightly less precise (it cannot distinguish pre-existing funds) but ensures
        // that PUBLIC on-chain funds are not silently ignored, which is critical for
        // beta testing on Mezo testnet.
        console.log('📊 ===== PAYMENT DETECTION (NO SNAPSHOT FALLBACK) =====');
        console.log('❌ No transaction history and no reliable balance snapshot available.');
        console.log('   Current balance:', currentBalance.toString(), 'wei');
        console.log('   Requested amount (wei):', requestedAmountWei.toString());
        
        if (requestedAmountWei > 0n && currentBalance >= requestedAmountWei) {
          console.log('✅ Fallback: Treating current balance as sufficient to cover requested amount.');
          // Cap to requested amount so we never expose the full wallet balance as “payment”.
          totalReceived = requestedAmountWei;
        } else {
          console.log('❌ Fallback check also failed – not enough balance to cover requested amount.');
          console.log('   💡 If payment was sent, Boar Network may need a few moments to index it.');
          console.log('   💡 Please try again in 10-30 seconds or verify the transaction on the explorer.');
          return { confirmed: false, amount: '0', error: 'No payment detected via transaction history or balance checks.' };
        }
      }
      
      if (totalReceived === 0n) {
        console.log('❌ No payment detected - totalReceived is 0');
        console.log('   Current balance:', currentBalance.toString(), 'wei');
        console.log('   Detected from transactions:', detectedPaymentAmount.toString(), 'wei');
        console.log('   Balance snapshot available:', hasValidBalanceSnapshot);
        console.log('   💡 If you sent BTC to this address, it may take a few moments for Boar Network to index it.');
        console.log('   💡 Please try again in 10-30 seconds or check the transaction on the explorer.');
        return { confirmed: false, amount: '0', error: 'No payment detected' };
      }

      // Now validate payment amount
      console.log('💰 ===== PAYMENT VALIDATION =====');
      console.log('💰 Total received (actual payment detected):', totalReceived.toString());
      console.log('💰 Requested amount (original):', invoice.requestedAmount);
      console.log('💰 Requested amount (wei):', requestedAmountWei.toString());
      console.log('💰 Is total received >= requested?', totalReceived >= requestedAmountWei);
      
      if (totalReceived >= requestedAmountWei) {
        console.log('✅ Payment confirmed! Amount received:', totalReceived.toString());
        
        // Try to get actual transaction hash from incoming transactions detected via RPC
        let actualTxHash = `boar_rpc_${Date.now()}`;
        let actualFrom = 'unknown';
        let actualBlockNumber = 0;
        let actualTimestamp = Date.now();
        let paymentAmount = totalReceived.toString(); // Default to totalReceived
        
        // Use incomingTxs from earlier in function scope
        if (incomingTxs.length > 0) {
          // Find the transaction that meets the amount requirement (prefer exact match)
          const exactMatchTx = incomingTxs.find(tx => BigInt(tx.value || '0') >= requestedAmountWei);
          const matchingTx = exactMatchTx || incomingTxs[0];
          
          actualTxHash = matchingTx.hash || actualTxHash;
          actualFrom = matchingTx.from || actualFrom;
          actualBlockNumber = matchingTx.blockNumber || actualBlockNumber;
          actualTimestamp = matchingTx.timestamp || actualTimestamp;
          
          // If we have a single transaction that meets the requirement, use its amount
          // Otherwise, if we have multiple transactions summing to the required amount, 
          // use the sum (totalReceived) but cap to requested amount to avoid showing more than requested
          if (exactMatchTx) {
            // Single transaction that meets requirement - use its amount
            paymentAmount = exactMatchTx.value || paymentAmount;
            console.log('📝 Using exact matching transaction amount:', paymentAmount);
          } else if (totalReceived > requestedAmountWei) {
            // Multiple transactions - cap to requested amount to show only the payment, not full balance
            paymentAmount = requestedAmountWei.toString();
            console.log('📝 Multiple transactions detected - capping to requested amount:', paymentAmount);
          } else {
            // Sum of transactions equals requested - use the sum
            paymentAmount = totalReceived.toString();
            console.log('📝 Using sum of all transactions:', paymentAmount);
          }
          
          console.log('📝 Using actual RPC transaction hash from Boar:', actualTxHash);
        } else {
          // No transaction history - cap payment amount to requested amount
          // This prevents showing full wallet balance when using balance increase fallback
          if (totalReceived > requestedAmountWei) {
            paymentAmount = requestedAmountWei.toString();
            console.log('⚠️ Capping payment amount to requested amount (no tx history):', paymentAmount);
          }
        }
        
        // Create a transaction record for the payments section with actual RPC data
        // Note: This will be returned to the caller who will store it with creator address
        const transactionRecord: StoredTransaction = {
          txHash: actualTxHash,
          invoiceId: invoice.id,
          from: actualFrom,
          to: paymentAddress,
          amount: paymentAmount, // Use actual transaction amount, not totalReceived (to avoid showing full wallet balance)
          blockNumber: actualBlockNumber,
          timestamp: actualTimestamp,
          confirmations: 1, // RPC confirmation counts as 1 confirmation
          status: 'confirmed',
          detectedAt: Date.now(),
        };
        
        // Return transaction record - caller will store it with creator address
        // Return the actual payment amount, not totalReceived (which might be larger)
        return { confirmed: true, amount: paymentAmount, transaction: transactionRecord };
      } else {
        console.log('⚠️ Partial payment received:', totalReceived.toString(), 'Expected:', requestedAmountWei.toString());
        console.log('   Shortfall:', (requestedAmountWei - totalReceived).toString(), 'wei');
        return { confirmed: false, amount: totalReceived.toString(), error: 'PARTIAL_PAYMENT', shortfall: (requestedAmountWei - totalReceived).toString() };
      }
      
    } catch (error) {
      console.error('🚨 Error confirming payment:', error);
      return { confirmed: false, amount: '0', error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Start monitoring all pending invoices
  // NOTE: This method is not currently used since all invoices are on-chain and monitoring
  // is handled via useEffect in useInvoiceContract. Keeping for backward compatibility.
  startMonitoringPendingInvoices(address?: string | null): void {
    if (!address) {
      console.warn('⚠️ startMonitoringPendingInvoices called without address - skipping');
      return;
    }
    console.log('👀 Starting to monitor all pending invoices...');
    
    const pendingInvoices = invoiceStorage.listDrafts(address).filter(invoice => invoice.status === 'pending');
    
    pendingInvoices.forEach(invoice => {
      if (invoice.payToAddress) {
        this.subscribeToAddress({
          address: invoice.payToAddress,
          invoiceId: invoice.id,
          expectedAmount: invoice.requestedAmount,
        });
      }
    });
  }

  // Stop monitoring all invoices
  stopMonitoringAllInvoices(): void {
    console.log('🛑 Stopping monitoring for all invoices...');
    
    this.subscriptions.forEach((_subscription, address) => {
      this.unsubscribeFromAddress(address);
    });
  }

  // Check and update expired invoices
  // Note: This method requires wallet address - should be called from useInvoiceContract with address
  // This method is kept for backward compatibility but may not work correctly without address
  checkExpiredInvoices(walletAddress?: string): void {
    if (!walletAddress) {
      console.warn('⚠️ checkExpiredInvoices called without wallet address - skipping');
      return;
    }
    const expiredInvoices = invoiceStorage.getExpiredInvoices(walletAddress);
    
    expiredInvoices.forEach(invoice => {
      console.log('⏰ Marking invoice as expired:', invoice.id);
      invoiceStorage.markAsExpired(walletAddress, invoice.id);
      
      // Unsubscribe from monitoring this address
      if (invoice.payToAddress) {
        this.unsubscribeFromAddress(invoice.payToAddress);
      }
    });
  }

  // Disconnect and cleanup
  disconnect(): void {
    console.log('👋 Disconnecting from Boar Network...');
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.isConnected = false;
    this.subscriptions.clear();
  }
}

// Export singleton instance
export const paymentMonitor = new PaymentMonitorService();
